import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { getPreloadMargin, scheduleBackgroundTask } from '@theme/loading-scheduler';

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver((entries, observer) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    this.#loadRecommendations();
  }, { rootMargin: `${getPreloadMargin()}px` });

  #viewportObserver = new IntersectionObserver((entries) => {
    this.#visible = entries.some((entry) => entry.isIntersecting);
    if (!this.#visible) return;
    this.#scheduled?.promote();
    this.#loadRecommendations(true);
  });

  #mutationObserver = new MutationObserver((mutations) => {
    const key = this.#configurationKey();
    const changedConfiguration = key !== this.#configuration;
    const reset = mutations.some((mutation) => mutation.attributeName === 'data-recommendations-performed') &&
      this.dataset.recommendationsPerformed !== 'true';
    if (!changedConfiguration && !reset) return;
    // A second callback for our own reset must not restart the active request.
    if (!changedConfiguration && this.#activeFetch) return;
    this.#configuration = key;
    this.#invalidate();
    if (changedConfiguration) this.dataset.recommendationsPerformed = 'false';
    this.#loadRecommendations(this.#visible);
  });

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {{ promote: () => void } | null} */
  #scheduled = null;
  #generation = 0;
  #visible = false;
  #configuration = '';

  connectedCallback() {
    super.connectedCallback();
    this.#configuration = this.#configurationKey();
    this.#intersectionObserver.observe(this);
    this.#viewportObserver.observe(this);
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-section-id', 'data-intent', 'data-url', 'data-recommendations-performed'],
    });
    document.addEventListener('shopify:section:load', this.#handleEditorReload);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#invalidate();
    this.#visible = false;
    this.#intersectionObserver.disconnect();
    this.#viewportObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener('shopify:section:load', this.#handleEditorReload);
  }

  #configurationKey() {
    const { productId, sectionId, intent, url } = this.dataset;
    return [productId, sectionId, intent, url].join('\u0000');
  }

  #invalidate() {
    this.#generation += 1;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#scheduled = null;
  }

  /** @param {Event} event */
  #handleEditorReload = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#invalidate();
    this.#cachedRecommendations = {};
    this.dataset.recommendationsPerformed = 'false';
    this.#loadRecommendations(true);
  };

  /** @param {boolean} [foreground] */
  #loadRecommendations(foreground = false) {
    if (!this.isConnected || this.dataset.recommendationsPerformed === 'true') return;
    if (this.#activeFetch) {
      if (foreground) this.#scheduled?.promote();
      return;
    }
    const { productId, sectionId, intent, url: baseUrl } = this.dataset;
    if (!productId || !sectionId || !baseUrl) {
      this.#handleError(new Error('Product ID, section ID and recommendations URL are required'));
      return;
    }
    const url = new URL(baseUrl, window.location.href);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('section_id', sectionId);
    if (intent) url.searchParams.set('intent', intent);
    const controller = new AbortController();
    this.#activeFetch = controller;
    const generation = this.#generation;
    const isCurrent = () => this.isConnected && generation === this.#generation && !controller.signal.aborted;
    const scheduled = scheduleBackgroundTask(async () => {
      const cached = this.#cachedRecommendations[url.href];
      if (cached !== undefined) return { success: true, data: cached };
      const response = await fetch(url.href, { signal: controller.signal });
      if (!response.ok) return { success: false, status: response.status };
      const data = await response.text();
      controller.signal.throwIfAborted();
      this.#cachedRecommendations[url.href] = data;
      return { success: true, data };
    }, { signal: controller.signal });
    this.#scheduled = scheduled;
    if (foreground || this.#visible) scheduled.promote();
    void scheduled.promise.then(async (result) => {
      if (!isCurrent()) return;
      if (!result.success) {
        if (!Shopify.designMode) this.#handleError(new Error(`Server returned ${result.status}`));
        return;
      }
      if (!result.data?.trim().length) {
        this.#handleError(new Error('No recommendations available'));
        return;
      }
      await morphSection(sectionId, result.data, {
        mode: 'hydration', injectStylesheet: true, signal: controller.signal,
      });
      if (!isCurrent()) return;
      this.dataset.recommendationsPerformed = 'true';
      delete this.dataset.error;
    }).catch((error) => {
      if (isCurrent()) this.#handleError(error);
    }).finally(() => {
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
        this.#scheduled = null;
      }
    });
  }

  /** @param {Error} error */
  #handleError(error) {
    if (error.name === 'AbortError') return;
    this.dataset.error = 'Error loading product recommendations';
    this.dispatchEvent(new CustomEvent('recommendations:error', { bubbles: true, detail: { error } }));
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
